import { useLazyQuery, useMutation, useQuery } from '@apollo/client';

import { IEntitiesWithMembersResult, IEntityMemberParams, IEntityMemberResults } from './entity-members.types';
import { IEntityMember, Identifiable } from '../../../lib/types';
import { useCallback, useMemo } from 'react';
import { updateEntityMemberCache } from './entity-members.utils';
import {
  DELETE_ENTITY_MEMBER,
  GET_ENTITIES_MEMBERS,
  GET_ENTITY_MEMBERS,
  RESTORE_ENTITY_MEMBER
} from './entity-members.queries';

export const useEntityMembers = (variables: IEntityMemberParams) => {
  const { data, loading, error, called, refetch, networkStatus, client, observable, previousData } = useQuery<IEntityMemberResults>(GET_ENTITY_MEMBERS, {
    fetchPolicy: 'cache-and-network',
    variables
  });

  const entityMembers = useMemo(() => data?.entityMembers, [data?.entityMembers]);

  return useMemo(() => ({
    loading,
    error,
    called,
    refetch,
    networkStatus,
    client,
    observable,
    previousData,
    entityMembers
  }), [loading, error, called, refetch, networkStatus, client, observable, previousData, entityMembers]);
};

export const useEntitiesMembers = () => {
  // TODO: Could update useEntityMembers cache results here with entities members
  const { data, loading, error, called, refetch, networkStatus, client, observable, previousData } = useQuery<IEntitiesWithMembersResult>(GET_ENTITIES_MEMBERS, {
    fetchPolicy: 'cache-and-network',
    variables: { includeArchived: true }
  });

  const entitiesMembers = useMemo(() => data?.entitiesMembers, [data?.entitiesMembers]);

  return useMemo(() => ({
    loading,
    error,
    called,
    refetch,
    networkStatus,
    client,
    observable,
    previousData,
    entitiesMembers
  }), [loading, error, called, refetch, networkStatus, client, observable, previousData, entitiesMembers]);
};

export const useLazyEntityMembers = () => {
  const [query, { data, loading, error, called, refetch, networkStatus, client, observable, previousData }] = useLazyQuery<IEntityMemberResults>(GET_ENTITY_MEMBERS, {
    fetchPolicy: 'cache-and-network'
  });

  const fetchEntityMembers = useCallback((variables: IEntityMemberParams) => query({ variables }), [query]);
  const entityMembers = useMemo(() => data?.entityMembers, [data?.entityMembers]);

  return useMemo(() => ({
    fetchEntityMembers,
    loading,
    error,
    called,
    refetch,
    networkStatus,
    client,
    observable,
    previousData,
    entityMembers
  }), [fetchEntityMembers, loading, error, called, refetch, networkStatus, client, observable, previousData, entityMembers]);
};

export const useDeleteEntityMember = () => {
  const [mutation, rest] = useMutation<{ deleteEntityMember: IEntityMember }, Identifiable>(DELETE_ENTITY_MEMBER, {
    context: { serializationKey: 'MUTATION', tracked: true },
    update: (cache, { data }) => {
      if (data) {
        updateEntityMemberCache({
          cache,
          entityMembers: [data.deleteEntityMember],
          variables: { entityId: data.deleteEntityMember.entity._id }
        });

        updateEntityMemberCache({
          cache,
          entityMembers: [data.deleteEntityMember],
          variables: { userId: data.deleteEntityMember.user._id }
        });
      }
    }
  });

  const deleteEntityMember = useCallback((variables: Identifiable) => mutation({ variables }), [mutation]);

  return { deleteEntityMember, ...rest };
};

export const useRestoreEntityMember = (variables: IEntityMemberParams) => {
  const [mutation, rest] = useMutation<{ restoreEntityMember: IEntityMember }, Identifiable>(RESTORE_ENTITY_MEMBER, {
    context: { serializationKey: 'MUTATION', tracked: true },
    update: (cache, { data }) => {
      if (data) updateEntityMemberCache({ cache, entityMembers: [data.restoreEntityMember], variables });
    }
  });

  const restoreEntityMember = useCallback((variables: Identifiable) => mutation({ variables }), [mutation]);

  return { restoreEntityMember, ...rest };
};
